import { Box, Paper } from "@mui/material";
import {
  IconDeviceMobile,
  IconShare2,
  IconSquarePlus,
} from "@tabler/icons-react";
import { usePwaInstallPrompt } from "../../hooks/usePwaInstallPrompt";
import {
  isIosSafari,
  isMobileUserAgent,
  isStandalonePwa,
} from "../../utils/pwa";
import { promptPwaInstall } from "../../utils/pwaInstallPrompt";
import Button from "../ui/Button";
import Text from "../ui/Text";

/**
 * Inline (non-dismissible) install callout for mobile web visitors, meant to
 * be embedded in page content — e.g. the game success screen — rather than
 * floating over the whole app like InstallPrompt's global banner. Renders
 * nothing on desktop or once the app is already installed.
 *
 * Reads the shared beforeinstallprompt capture (see
 * utils/pwaInstallPrompt) instead of listening independently, since the
 * event only fires once per page load and this component is typically
 * reached via client-side navigation well after that - an independent
 * listener here would simply miss it.
 */
export default function MobileInstallCallout() {
  const { deferredPrompt, installed } = usePwaInstallPrompt();

  if (installed || isStandalonePwa() || !isMobileUserAgent()) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 2,
        borderRadius: "8px",
        backgroundColor: "#EAF4FF",
        border: "1px solid #BFE0FF",
        mb: 3,
      }}
    >
      <IconDeviceMobile
        size={22}
        color="#1890FF"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text variant="subtitle2" sx={{ mb: 0.5 }}>
          Get the FindASide app
        </Text>
        <Text variant="paragraph3" sx={{ color: "#555", mb: 1.5 }}>
          Add it to your home screen for quicker access, a full-screen view with
          no browser bars, and it still works with a patchy connection.
        </Text>
        {isIosSafari() ? (
          <Text
            variant="paragraph3"
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            Tap
            <IconShare2 size={14} style={{ verticalAlign: "middle" }} />
            then <b>Add to Home Screen</b>
            <IconSquarePlus size={14} style={{ verticalAlign: "middle" }} />
          </Text>
        ) : deferredPrompt ? (
          <Button
            variant="primary"
            onClick={() => promptPwaInstall()}
            sx={{ width: "auto", px: 3 }}
          >
            Install app
          </Button>
        ) : (
          <Text variant="paragraph3" sx={{ color: "#555" }}>
            Open your browser menu and choose &ldquo;Add to Home Screen&rdquo;
            or &ldquo;Install app.&rdquo;
          </Text>
        )}
      </Box>
    </Paper>
  );
}
